import { Auth } from "aws-amplify";
import { analyticsService } from "../../services/AnalyticsService";
import { AuthenticationDetails } from "amazon-cognito-identity-js";
import axios from "axios";
import jwt_decode from "jwt-decode";
import { baseUrl, loginUrl } from "../../utils/env";
import { reportError } from "../../utils/rollbar";
import { pushToDataLayer } from "../../utils/helperFunctions";

export const handleAction = (
  action: any,
  e: any,
  setPassword: any,
  setUserName: any,
  setGivenName: any,
  setFamilyName: any,
  setPhoneNumber: any,
  setCountryCode: any,
  setRecaptchaToken: any,
  setCompanyName: any
) => {
  switch (action) {
    case "password":
      setPassword(e.currentTarget.value);
      break;
    case "email":
      setUserName(e.currentTarget.value);
      break;
    case "firstName":
      setGivenName(e.currentTarget.value);
      break;
    case "lastName":
      setFamilyName(e.currentTarget.value);
      break;
    case "phoneNumber":
      setPhoneNumber(e.currentTarget.value);
      break;
    case "countryCode":
      setCountryCode(e.currentTarget.value);
      break;
    case "companyName":
      setCompanyName(e.currentTarget.value);
      break;
    case "captcha":
      setRecaptchaToken(e);
      break;
    default:
      break;
  }
};

const getUserAgent = () => {
  return navigator.userAgent;
};

const redirectUser = (startTime: Date, redirectUrl: any = null) => {
  const endTime = new Date();
  const timeElapsed = Math.abs(endTime.getTime() - startTime.getTime());
  analyticsService.track("SES - Time elapsed from code input to redirect", { timeElapsed: timeElapsed / 1000 }, null);
  if (redirectUrl) {
    window.location.href = redirectUrl;
  } else if (baseUrl) {
    window.location.href = baseUrl;
  } else {
    console.error("Base URL is not defined");
    // Optionally handle this case further e.g. display an error to the user
  }
};

export const confirmSignUp = async (code: any, cognitoUser: any) => {
  try {
    await Auth.confirmSignUp(cognitoUser.username, code);
  } catch (error) {
    console.log("1 - Error", error);
    analyticsService.trackError(error, cognitoUser.username);
    reportError("Failed to confirm signup", error);
    throw error; 
  }
};

export const authenticate = async (Password: any, CognitoUser: any, Username: any, userType: any, startTime: any, url: any, redirectUrl: any) => {
  await CognitoUser.setAuthenticationFlowType("USER_PASSWORD_AUTH");

  const authDetails = new AuthenticationDetails({
    Username,
    Password,
  });

  return new Promise<void>((resolve, reject) => { 
    CognitoUser.authenticateUser(authDetails, {
      onSuccess: async (data: any) => {
        console.log("2 - Authenticate User", authDetails, data);
        if (userType === "selfService") {
          window.dataLayer.push({event: "signup_completion_success",
              user_id: JSON.parse(data.idToken.payload["custom:platform_data"]).id,
              method: "email"
          });

          let config = {
            method: "post",
            url: `${loginUrl}/api/v2/user/session`,
            withCredentials: true,
            headers: { "X-Requested-With": "XMLHttpRequest" },
            data: {
              cognito_jwt_token: data.idToken.jwtToken,
              userAgent: getUserAgent(),
              isRegistration: true,
              current_url: url,
              phone_collected: true, // to allow using session endpoint
            },
          };
          try {
            await axios(config);
            analyticsService.track("SES - User Signed Up Successfully", { Username }, redirectUser(startTime, redirectUrl));
            resolve(); 
          } catch (err) {
            console.log("Error on Session API: ", err);
            reportError("Session API Error", err);
            reject(err); 
          }
        }
      },
      onFailure: (error: any) => {
        console.log("2 - Error", error);
        reportError("Failed to authenticate user", error);
        reject(error); 
      },
    });
  });
};

export const checkStatesAndPhoneNumber = (states: any, phoneNumber: any, userType: any, step: any, setState: any) => {
  if (states.some((state: any) => !state)) {
    // if any state is falsy, do not set the state
    setState(true);
    return;
  }

  if (userType === "selfService") {
    // if user type is "selfService", do not check phone number
    setState(false);
    return;
  }

  if (userType === "candidate" && step === "stepTwo") {
    setState(false);
  } else if (phoneNumber && phoneNumber.length < 8) {
    return;
  }

  // all states are truthy and phone number is at least 8 characters long (if applicable)
  setState(false);
};

export const checkUserCreationCompletion = async (setLoading: any, setError: any, Password: any, CognitoUser: any, Username: any) => {
  let startTime = new Date().getTime();
  const authDetails = new AuthenticationDetails({
    Username,
    Password,
  });

  // This function checks the creation_info field in the JWT token to see if the user creation process has completed on platform side
  const checkCreationInfo = async () => {
    return new Promise<void>((resolve, reject) => {
      CognitoUser.authenticateUser(authDetails, {
        onSuccess: async (data: any) => {
          const decodedToken = jwt_decode(data.idToken.jwtToken);
          const customPlatformData = JSON.parse((decodedToken as any)["custom:platform_data"]);
          const creationInfo = customPlatformData.creation_info;
          const platformId = customPlatformData.id;
          if (platformId) {
            analyticsService.identify(platformId);
            pushToDataLayer({
              event: "sign_up",
              method: "Email",
              user_id: platformId,
            });
          }
          if (!creationInfo) {
            resolve();
            return;
          }
          const currentTime = new Date().getTime();
          if (currentTime - startTime >= 10000) {
            reject(new Error("User creation timed out after 10 seconds"));
          } else {
            setTimeout(() => {
              checkCreationInfo().then(resolve).catch(reject);
            }, 1000);
          }
        },
        onFailure: (error: any) => {
          reject(error);
        },
      });
    });
  };

  try {
    setLoading(true);
    await checkCreationInfo();
  } catch (error) {
    setError(error);
    setLoading(false);
  }
};
